import React, { useEffect, useState } from 'react';
import './storefrontNotice.css';

// What the storefront shows when the address names no shop.
//
// It wears the sign-in screen's shape — one card centred on a muted field, the message on one side
// and a live panel on the other — because that screen is the platform's own front door and this is
// the same kind of moment. Self-contained by necessity: it renders when no theme exists to style it.
//
// It names no subdomain scheme. Shopify answers an unknown shop with a bare 404 and no explanation;
// telling a stranger how stores are addressed only tells them where to go looking, and whoever
// mistyped a link cannot act on the answer. What they can act on is opening a shop of their own.

/** Compiled in by a deployment that chose to; normally unset, so the server or the host answers. */
const BUILT_IN_ADMIN = String(import.meta.env.VITE_ADMIN_URL ?? '').replace(/\/+$/, '');

/** The page a merchant lands on: it signs in, and offers "Create one" to whoever has no store yet. */
const ADMIN_ENTRY = '/sign-in';

// Asks the API where the dashboard lives. Returns '' when it cannot be reached, which is the case
// on a host whose ingress does not route /api — there the sibling host is the only answer left.
async function fetchAdminOrigin(): Promise<string> {
  try {
    const res = await fetch('/api/v1/platform', { headers: { Accept: 'application/json' } });
    if (!res.ok) return '';
    const body: unknown = await res.json();
    const url = (body as { data?: { adminUrl?: unknown } })?.data?.adminUrl;
    return typeof url === 'string' ? url.replace(/\/+$/, '') : '';
  } catch {
    return '';
  }
}

// Resolves the admin's origin: what this build was told, else what the server says, else the host
// this page is served on with its app label swapped. There is always an answer, so always a way out.
function useAdminOrigin(): string {
  const [origin, setOrigin] = useState(BUILT_IN_ADMIN);

  useEffect(() => {
    if (BUILT_IN_ADMIN) return;
    let cancelled = false;
    void fetchAdminOrigin().then((found) => { if (found && !cancelled) setOrigin(found); });
    return () => { cancelled = true; };
  }, []);

  return origin;
}

/** How often the panel takes another reading — slow enough to watch, quick enough to feel live. */
const TICK_MS = 2400;

/** A plausible day's shape, so the chart reads as trade rather than as noise. */
const SHAPE = [38, 52, 46, 64, 58, 79, 71, 88, 76, 94, 83, 100];

// Advances a counter forever, which is what makes the panel feel live rather than drawn.
function useTick(intervalMs: number): number {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);

  return tick;
}

// The panel beside the message: a store's day, mid-flight. Nothing starts and nothing finishes —
// the series shifts a column at a time and the figures only climb, so arriving at any moment looks
// like a shop already trading.
const LivePanel: React.FC = () => {
  const tick = useTick(TICK_MS);
  const revenue = 12480 + tick * 137;
  const orders = 128 + tick;
  const delivered = 96 + tick;

  return (
    <div className="sf-panel" aria-hidden="true">
      <div className="sf-live">
        <div className="sf-live-head">
          <strong>Today</strong>
          <span className="sf-dot"><i />live</span>
        </div>

        <p className="sf-amount"><span>Revenue</span>MAD {revenue.toLocaleString('en-US')}</p>

        <div className="sf-chart">
          {SHAPE.map((_, column) => (
            <b key={column} style={{ height: `${SHAPE[(column + tick) % SHAPE.length]}%` }} />
          ))}
        </div>

        <div className="sf-stats">
          <div><small>Orders</small><em>{orders}</em></div>
          <div><small>Delivered</small><em>{delivered}</em></div>
          <div><small>Collected</small><em>{Math.round((delivered / orders) * 100)}%</em></div>
        </div>
      </div>

      <div className="sf-blurb">
        <strong>Your store, as it happens</strong>
        <p>Orders placed, parcels delivered, cash collected — counted the moment each one lands.</p>
      </div>
    </div>
  );
};

interface StorefrontNoticeProps {
  title: string;
  detail: string;
}

// Renders the notice and the one thing its visitor can act on.
export const StorefrontNotice: React.FC<StorefrontNoticeProps> = ({ title, detail }) => {
  const adminOrigin = useAdminOrigin();

  return (
  <div className="sf-page" role="status">
    <div className="sf-card">
      <div className="sf-say">
        <img className="sf-mark" src="/xshop-mark.svg" width={36} height={36} alt="" />
        <h1>{title}</h1>
        <p>{detail}</p>
        {adminOrigin ? (
        <a className="sf-cta" href={`${adminOrigin}${ADMIN_ENTRY}`}>
          Create your store
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M5 12h13m0 0-5.5-5.5M18 12l-5.5 5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
        ) : null}
      </div>
      <LivePanel />
    </div>
  </div>
  );
};
